import type { SearchResponse } from '@monrdv/shared';
import { apiPath } from './tenant.js';

/**
 * API client.
 *
 * Thin on purpose: one `request` helper, typed per endpoint. The interesting decisions are
 * in error handling, because how a data-entry application behaves when a request fails is
 * the difference between a coordinator trusting it and not.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Present on a 403, naming the permission the account lacks. */
    readonly capability?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `apiPath` adds the business slug to everything except /auth, /me and /health — in one
  // place, so no caller has to remember and a new endpoint cannot quietly get it wrong.
  const response = await fetch(apiPath(path), {
    ...init,
    // The session is an httpOnly cookie, so every call must carry credentials.
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...init?.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    // 401 means the session is gone. Reloading sends the user to sign-in without every
    // caller having to handle it, and the router restores where they were afterwards.
    if (response.status === 401 && !path.startsWith('/auth')) {
      window.location.assign(`/connexion?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
    throw new ApiError(
      response.status,
      body?.error ?? 'request_failed',
      body?.message ?? response.statusText,
      body?.capability,
    );
  }

  return body as T;
}

export const api = {
  health: () => request<{ ok: boolean; connections: number }>('/health'),

  search: (params: { q: string; kinds?: string[]; limit?: number; grouped?: boolean }, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ q: params.q });
    if (params.kinds?.length) for (const k of params.kinds) qs.append('kinds', k);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.grouped !== undefined) qs.set('grouped', String(params.grouped));
    return request<SearchResponse>(`/search?${qs}`, { signal });
  },

  searchNearby: (params: { latitude: number; longitude: number; radiusKm?: number; mine?: boolean }) => {
    const qs = new URLSearchParams({
      latitude: String(params.latitude),
      longitude: String(params.longitude),
    });
    if (params.radiusKm) qs.set('radiusKm', String(params.radiusKm));
    if (params.mine) qs.set('mine', 'true');
    return request<{
      origin: { latitude: number; longitude: number };
      radiusKm: number;
      jobs: Array<{
        jobId: string;
        customerName: string;
        addressLine: string | null;
        city: string | null;
        distanceKm: number;
        scheduledStart: string | null;
        stageName: Record<string, string> | null;
      }>;
    }>(`/search/nearby?${qs}`);
  },
};

export { request };
